import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  GET_EMPLOYEE,
  STORE_JOB_ROLES,
  STORE_STAFF_JOB_ROLES_BY_STORE,
  ASSIGN_STORE_STAFF_JOB_ROLE,
  REMOVE_STORE_STAFF_JOB_ROLE,
  SET_STORE_STAFF_JOB_ROLE_PRIMARY,
} from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import type { StoreEmployeeOption, StoreJobRole, StoreStaffJobRole } from "../types/payroll.types";

export function EmployeeJobRolesTab() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [employees, setEmployees] = useState<StoreEmployeeOption[]>([]);
  const [allAssignments, setAllAssignments] = useState<StoreStaffJobRole[]>([]);
  const [jobRoles, setJobRoles] = useState<StoreJobRole[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [assignEmployeeId, setAssignEmployeeId] = useState<number | null>(null);
  const [assignRoleId, setAssignRoleId] = useState<string>("");
  const [assignDuplicateError, setAssignDuplicateError] = useState<string | null>(null);

  const employeesWithStaffId = employees.filter((e) => e.storeStaffId != null && e.storeStaffId > 0);

  const employeeNameByStaffId = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesWithStaffId) {
      if (e.storeStaffId == null) continue;
      const name = [e.firstName, e.lastName].filter(Boolean).join(" ") || e.email || e.correlationId;
      map.set(e.storeStaffId, name);
    }
    return map;
  }, [employeesWithStaffId]);

  const fetchEmployees = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeEmployeesByStoreCorrelationId?: StoreEmployeeOption[] } }>(
        GRAPHQL_ENDPOINT,
        { query: GET_EMPLOYEE, variables: { storeCorrelationId: hasRestaurant.correlationId } }
      );
      setEmployees(data?.data?.storeEmployeesByStoreCorrelationId ?? []);
    } catch {
      setEmployees([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchJobRoles = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeJobRoles?: StoreJobRole[] } }>(GRAPHQL_ENDPOINT, {
        query: STORE_JOB_ROLES,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: true },
      });
      setJobRoles(data?.data?.storeJobRoles ?? []);
    } catch {
      setJobRoles([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchAllAssignments = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoadingList(true);
    setError(null);
    try {
      const { data } = await axBe.post<{
        data?: { storeStaffJobRolesByStore?: StoreStaffJobRole[] };
      }>(GRAPHQL_ENDPOINT, {
        query: STORE_STAFF_JOB_ROLES_BY_STORE,
        variables: { storeCorrelationId: hasRestaurant.correlationId },
      });
      setAllAssignments(data?.data?.storeStaffJobRolesByStore ?? []);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to load assignments");
      setAllAssignments([]);
    } finally {
      setLoadingList(false);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  useEffect(() => {
    fetchEmployees();
    fetchJobRoles();
    fetchAllAssignments();
  }, [fetchEmployees, fetchJobRoles, fetchAllAssignments]);

  const assignmentsForAssignEmployee =
    assignEmployeeId != null ? allAssignments.filter((a) => a.storeStaffId === assignEmployeeId) : [];
  const alreadyAssignedRoleIds = new Set(assignmentsForAssignEmployee.map((r) => r.storeJobRoleId));
  const availableToAssign = jobRoles.filter((j) => !alreadyAssignedRoleIds.has(j.id));
  const canAssign = assignEmployeeId != null && assignEmployeeId > 0 && assignRoleId !== "";

  const groupedByStaff = useMemo(() => {
    const map = new Map<number, StoreStaffJobRole[]>();
    for (const a of allAssignments) {
      const list = map.get(a.storeStaffId) ?? [];
      list.push(a);
      map.set(a.storeStaffId, list);
    }
    Array.from(map.values()).forEach((list) => {
      list.sort((a: StoreStaffJobRole, b: StoreStaffJobRole) => (a.isPrimary ? -1 : b.isPrimary ? 1 : 0));
    });
    const entries = Array.from(map.entries());
    entries.sort(([idA], [idB]) => {
      const nameA = employeeNameByStaffId.get(idA) ?? "";
      const nameB = employeeNameByStaffId.get(idB) ?? "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
    return entries;
  }, [allAssignments, employeeNameByStaffId]);

  const summaryStats = useMemo(() => {
    const totalWithRoles = groupedByStaff.length;
    const withMultipleRoles = groupedByStaff.filter(([, list]) => list.length > 1).length;
    return { totalWithRoles, withMultipleRoles };
  }, [groupedByStaff]);

  const handleAssign = async () => {
    if (!canAssign) return;
    const roleId = parseInt(assignRoleId, 10);
    if (Number.isNaN(roleId)) return;
    if (alreadyAssignedRoleIds.has(roleId)) {
      setAssignDuplicateError("This role is already assigned to this employee.");
      return;
    }
    setAssignDuplicateError(null);
    const isPrimary = assignmentsForAssignEmployee.length === 0;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: ASSIGN_STORE_STAFF_JOB_ROLE,
        variables: { storeStaffId: assignEmployeeId, storeJobRoleId: roleId, isPrimary },
      });
      setAssignRoleId("");
      fetchAllAssignments();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to assign");
    }
  };

  const handleSetPrimary = async (storeStaffId: number, storeJobRoleId: number) => {
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: SET_STORE_STAFF_JOB_ROLE_PRIMARY,
        variables: { storeStaffId, storeJobRoleId },
      });
      fetchAllAssignments();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to set primary");
    }
  };

  const handleRemove = async (storeStaffId: number, storeJobRoleId: number) => {
    if (!window.confirm("Remove this role from the employee?")) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: REMOVE_STORE_STAFF_JOB_ROLE,
        variables: { storeStaffId, storeJobRoleId },
      });
      fetchAllAssignments();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to remove");
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <h3 className="text-sm font-medium text-[#374151] mb-2">Assign role to employee</h3>
      <div className="flex flex-wrap items-end gap-2 mb-2">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-0.5">Employee</label>
          <select
            value={assignEmployeeId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setAssignEmployeeId(v ? parseInt(v, 10) : null);
              setAssignRoleId("");
              setAssignDuplicateError(null);
            }}
            className="border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm min-w-[180px]"
          >
            <option value="">Select employee</option>
            {employeesWithStaffId.map((emp) => (
              <option key={emp.correlationId} value={emp.storeStaffId!}>
                {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.email || emp.correlationId}
              </option>
            ))}
          </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-0.5">Role</label>
            <select
              value={assignRoleId}
              onChange={(e) => {
                setAssignRoleId(e.target.value);
                setAssignDuplicateError(null);
              }}
              className="border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm min-w-[140px]"
            >
              <option value="">Select role</option>
              {availableToAssign.map((j) => (
                <option key={j.id} value={String(j.id)}>
                  {j.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={handleAssign}
          disabled={!canAssign}
          className="px-5 py-2.5 rounded-lg bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50"
        >
          Assign
        </button>
      </div>
      {assignDuplicateError && (
        <p className="text-sm text-red-600 mt-1 mb-2" role="alert">{assignDuplicateError}</p>
      )}
      {employees.length > 0 && employeesWithStaffId.length === 0 && (
        <p className="text-xs text-amber-600 mt-1">No employees with staff records found for this store.</p>
      )}

      <div className="mt-12">
        <h3 className="text-sm font-semibold text-[#374151] mb-2">Assigned roles</h3>
        {summaryStats.totalWithRoles > 0 && (
          <p className="text-xs text-[#6b7280] mb-3">
            Total employees with roles: {summaryStats.totalWithRoles}
            {summaryStats.withMultipleRoles > 0 && (
              <> · Employees with multiple roles: {summaryStats.withMultipleRoles}</>
            )}
          </p>
        )}
        {loadingList ? (
          <p className="text-sm text-[#6b7280]">Loading…</p>
        ) : groupedByStaff.length === 0 ? (
          <div className="rounded-xl border border-[#e5e7eb] bg-[#f9fafb] py-10 text-center text-sm text-[#6b7280]">
            No roles assigned yet. Assign a role above.
          </div>
        ) : (
          <div className="rounded-xl border border-[#e5e7eb] overflow-hidden divide-y divide-[#e5e7eb]">
            {groupedByStaff.map(([staffId, roles]) => (
              <div key={staffId} className="bg-white px-4 py-5 first:rounded-t-xl last:rounded-b-xl">
                <div className="font-medium text-[#111827] text-sm">
                  {employeeNameByStaffId.get(staffId) ?? `Staff #${staffId}`}
                </div>
                <p className="text-xs text-[#9ca3af] mt-0.5 mb-3">
                  {roles.length} {roles.length === 1 ? "role" : "roles"} assigned
                </p>
                <ul className="space-y-5">
                  {roles.map((r) => (
                    <li key={r.id} className="text-sm flex flex-wrap items-center gap-2">
                      {r.isPrimary ? (
                        <>
                          <span className="text-[#d1d5db] text-[10px] leading-none select-none">•</span>
                          <span className="text-[#374151] font-medium">
                            {r.jobRoleName ?? `Role #${r.storeJobRoleId}`}
                          </span>
                          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            Primary
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[#d1d5db] text-[10px] leading-none select-none">•</span>
                          <span className="text-[#374151] font-medium">
                            {r.jobRoleName ?? `Role #${r.storeJobRoleId}`}
                          </span>
                          <span className="ml-auto text-xs">
                            <button
                              type="button"
                              onClick={() => handleSetPrimary(r.storeStaffId, r.storeJobRoleId)}
                              className="text-[#6b7280] hover:text-[#374151] hover:underline transition-colors duration-150"
                            >
                              Set primary
                            </button>
                            <span className="mx-1.5 text-[#e5e7eb]">·</span>
                            <button
                              type="button"
                              onClick={() => handleRemove(r.storeStaffId, r.storeJobRoleId)}
                              className="text-red-700 hover:text-red-800 hover:underline transition-colors duration-150"
                            >
                              Remove
                            </button>
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
